import React, { useEffect, useState } from 'react';
import { updateUserSettings } from '../../settings';
import Layout from '../../components/Layout';
import { getUserInfo } from '../../user';
import { Navigate } from 'react-router-dom';
import api from '../../api';
import DataSearchFilters from '../../components/search/DataSearchFilters';
import useDataSearch from '../../hooks/useDataSearch';
import HostModal from '../../components/HostModal';
import MasterDataRowActions, { ArchivedBadge, ShowArchivedToggle } from '../../components/masterdata/ArchiveControls';
import { visibleRows } from '../../utils/archive';

// Searchable columns, as shown in the table
const SEARCH_FIELDS = [
  { key: 'name', label: 'Name', type: 'text', value: h => h.name },
  { key: 'address', label: 'Address', type: 'text', value: h => h.address },
  { key: 'phone', label: 'Phone', type: 'text', value: h => h.phone },
  { key: 'email', label: 'Email', type: 'text', value: h => h.email },
  { key: 'country', label: 'Country', type: 'select', value: h => h.country },
];

export default function Hosts() {
  const [user, setUser] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editHost, setEditHost] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const stored = sessionStorage.getItem('hostsPerPage');
    if (stored === 'all') return 'all';
    const num = stored ? Number(stored) : 15;
    return [15, 25, 50].includes(num) ? num : 15;
  });
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const shown = React.useMemo(() => visibleRows(hosts, showArchived), [hosts, showArchived]);
  const search = useDataSearch(shown, SEARCH_FIELDS, { onChange: () => setPage(1) });

  // Filter, sort, and paginate hosts (must be before any early return)
  const filtered = React.useMemo(() => {
    let data = search.filtered;
    if (sortKey) {
      data = [...data].sort((a, b) => {
        let v1 = a[sortKey] || '', v2 = b[sortKey] || '';
        v1 = typeof v1 === 'string' ? v1.toLowerCase() : v1;
        v2 = typeof v2 === 'string' ? v2.toLowerCase() : v2;
        if (v1 < v2) return sortDir === 'asc' ? -1 : 1;
        if (v1 > v2) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [search.filtered, sortKey, sortDir]);

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = React.useMemo(() => {
    if (pageSize === 'all') return filtered;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Persist page size
  React.useEffect(() => {
    sessionStorage.setItem('hostsPerPage', String(pageSize));
  }, [pageSize]);

  // Ensure page within bounds
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  useEffect(() => {
    getUserInfo().then(setUser);
  }, []);

  useEffect(() => { if (user && user.roles?.includes('admin')) fetchHosts(); }, [user]);

  function fetchHosts() {
    setLoading(true);
    setError('');
    api.post('/masterdata/hosts')
      .then(res => { setHosts(res.data); setPage(1); })
      .catch(() => setError('Failed to load hosts.'))
      .finally(() => setLoading(false));
  }

  function handleAdd() {
    setEditHost(null);
    setModalOpen(true);
  }
  function handleEdit(host) {
    setEditHost(host);
    setModalOpen(true);
  }

  if (!user) return null;
  if (!Array.isArray(user.roles) || !user.roles.includes('admin')) return <Navigate to="/" replace />;


  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  return (
    <Layout>
      <h1 className="text-2xl font-bold mb-4">Hosts</h1>
      <DataSearchFilters {...search.panelProps} />
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <ShowArchivedToggle checked={showArchived} onChange={setShowArchived} />
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 px-3 py-2 rounded flex items-center gap-1 border border-gray-300 dark:border-gray-600"
            onClick={fetchHosts}
            disabled={loading}
            title="Refresh"
          >
            <svg className={loading ? 'animate-spin w-4 h-4' : 'w-4 h-4'} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582M20 20v-5h-.581M5.582 9A7.974 7.974 0 0112 4c2.21 0 4.21.896 5.657 2.343M18.418 15A7.974 7.974 0 0112 20a7.974 7.974 0 01-5.657-2.343" /></svg>
            <span className="hidden sm:inline text-sm font-medium">Refresh</span>
          </button>
          <button className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow" onClick={handleAdd}>+ Add Host</button>
        </div>
        {error && <span className="text-red-600 ml-2">{error}</span>}
      </div>
      {loading ? (
        <div className="text-center text-gray-400">Loading hosts...</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow">
            <table className="min-w-full align-middle text-sm whitespace-nowrap border-separate border-spacing-0">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none" onClick={() => handleSort('name')}>Name {sortKey === 'name' && (<span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>)}</th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none" onClick={() => handleSort('address')}>Address {sortKey === 'address' && (<span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>)}</th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none" onClick={() => handleSort('phone')}>Phone {sortKey === 'phone' && (<span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>)}</th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none" onClick={() => handleSort('email')}>Email {sortKey === 'email' && (<span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>)}</th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none" onClick={() => handleSort('country')}>Country {sortKey === 'country' && (<span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>)}</th>
                  <th className="bg-gray-100/75 px-3 py-3 text-right font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-gray-400">No hosts found.</td></tr>
                ) : (
                  paginated.map(host => (
                    <tr key={host.id} className="border-b border-gray-100 dark:border-gray-700/50">
                      <td className="px-3 py-2 font-medium">{host.name}<ArchivedBadge row={host} /></td>
                      <td className="px-3 py-2">{host.address || '-'}</td>
                      <td className="px-3 py-2">{host.phone || '-'}</td>
                      <td className="px-3 py-2">{host.email || '-'}</td>
                      <td className="px-3 py-2">{host.country}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm leading-5 font-semibold text-gray-800 hover:border-gray-300 hover:text-gray-900 hover:shadow-xs focus:ring-3 focus:ring-gray-300/25 active:border-gray-200 active:shadow-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600/40 dark:active:border-gray-700"
                            onClick={() => handleEdit(host)}
                          >
                            Edit
                          </button>
                          <MasterDataRowActions type="hosts" row={host} name={host.name} noun="host" onChanged={fetchHosts} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-4 w-full">
            {pageSize !== 'all' && filtered.length > pageSize && (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="px-2 text-sm font-semibold">Page {page} of {totalPages}</span>
                <button
                  className="px-3 py-1 rounded border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
            <label className="inline-flex items-center gap-2 text-sm font-medium ml-auto">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={e => {
                  const val = e.target.value;
                  if (val === 'all') {
                    setPageSize('all');
                    setPage(1);
                    fetchHosts();
                    updateUserSettings({ hosts_per_page: 'all' });
                  } else {
                    const num = Number(val);
                    setPageSize(num);
                    setPage(1);
                    fetchHosts();
                    updateUserSettings({ hosts_per_page: num });
                  }
                }}
                className="form-select rounded border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
              >
                {[15, 25, 50].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
          </div>
        </>
      )}
      <HostModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={fetchHosts} host={editHost} />
    </Layout>
  );
}
